import { emailRegex } from '@awork/_shared/static-data/regular-expressions'

export type ISOLanguage = 'en-GB' | 'de-DE'

interface ExternalAccount {
  provider: string
  externalUserId: string
}

export interface AccountState {
  email: string
  refreshAt: Date
  refreshToken?: string
  accessToken?: string
  isImpersonated?: boolean
  id?: string
  firstName?: string
  lastName?: string
  language?: ISOLanguage
  timezone?: string
  passwordIsAutoGenerated?: boolean
  emailConfirmed?: boolean
  googleUserId?: string
  appleUserId?: string
  externalAccounts?: ExternalAccount[]
  mfaEnabled?: boolean
  mfaProviders?: string[]
  passwordlessEnabled?: boolean
}

export class Account {
  email: string
  isImpersonated = false
  refreshAt: Date
  id?: string
  firstName?: string
  lastName?: string
  language?: ISOLanguage
  timezone?: string
  passwordIsAutoGenerated: boolean
  googleUserId?: string
  appleUserId?: string
  externalAccounts?: ExternalAccount[]
  emailConfirmed?: boolean
  mfaEnabled?: boolean
  mfaProviders?: string[]
  passwordlessEnabled?: boolean

  constructor(email: string, expiresIn?: number, refreshAt?: Date) {
    this.email = email
    this.setIsImpersonated()

    if (expiresIn) {
      this.setRefreshAt(expiresIn)
    } else {
      this.refreshAt = refreshAt
    }
  }

  /**
   * Creates an Account object from a AccountState object
   * @param {AccountState} state
   * @returns {Account}
   */
  static fromState(state: AccountState): Account {
    const account = new Account(state.email)
    return Object.assign(account, state)
  }

  /**
   * Sets if the current account is an impersonation
   */
  setIsImpersonated() {
    if (this.email && this.email.includes('-AW-')) {
      const emails = this.email.split('-AW-')

      // Check if there are 2 valid emails
      if (emails.length === 2 && emailRegex.test(emails[0]) && emailRegex.test(emails[1])) {
        this.isImpersonated = true
      }
    }
  }

  /**
   * Gets the impersonator's email (the user performing the impersonation)
   * Returns the email before the -AW- separator, or the original email if not impersonated
   * @param {string} email The email to extract from
   * @returns {string}
   */
  static getImpersonatorEmail(email: string): string {
    return Account.extractImpersonationEmail(email, 0)
  }

  /**
   * Gets the impersonated account's email (the user being impersonated)
   * Returns the email after the -AW- separator, or the original email if not impersonated
   * @param {string} email The email to extract from
   * @returns {string}
   */
  static getImpersonatedEmail(email: string): string {
    return Account.extractImpersonationEmail(email, 1)
  }

  /**
   * Extracts an email from the impersonation format (email1-AW-email2)
   * @param {string} email The email string to extract from
   * @param {0 | 1} index 0 for impersonator email, 1 for impersonated email
   * @returns {string} The extracted email or the original email if not in impersonation format
   */
  private static extractImpersonationEmail(email: string, index: 0 | 1): string {
    // Remove ^ and $ anchors from emailRegex.source to use in capturing groups
    const emailPattern = emailRegex.source.slice(1, -1)
    const awEmailRegex = new RegExp(`^(${emailPattern})-AW-(${emailPattern})$`)
    const match = email?.match(awEmailRegex)

    return match?.[index + 1] ?? email
  }

  /**
   * Set the time when the session should be refreshed
   * @param {number} expiresIn Time in seconds
   */
  setRefreshAt(expiresIn: number) {
    const now = new Date()
    this.refreshAt = new Date(now.getTime() + expiresIn * 1000)
  }

  get formattedTimezone(): string {
    return this.timezone ? this.timezone.replace(/_/g, ' ') : 'Europe/Berlin'
  }

  get formattedLanguage(): string {
    switch (this.language) {
      case 'en-GB':
        return 'English'
      case 'de-DE':
        return 'Deutsch'
    }
  }
}
